import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { getAircraftStatusReport, getComplaints, getComplaint, getComplaintActions, updateComplaint } from '../services/api';
import openComplaintsIcon from '../assets/icons/opencomplaints.svg';
import mccIcon from '../assets/icons/mcc.svg';
import cabinItemsIcon from '../assets/icons/cabinitems.svg';
import arrowLeftIcon from '../assets/icons/arrow-sm-left.svg';
import './AircraftStatusReport.css';

const FILTER_OPTIONS = [
  { id: 'work_order_number', label: 'Workorder / Complaint Number' },
  { id: 'section', label: 'Last Deferral Type' },
  { id: 'status', label: 'Status' },
  { id: 'mcc_reference', label: 'Temporary Workorder Number' },
  { id: 'ata_chapter', label: 'ATA Chapter' },
  { id: 'type', label: 'Type of Workorder' }
];

const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const Chevron = () => <span className="asr-chevron" aria-hidden="true">›</span>;

const formatDisplayDate = (value) => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '---' : format(date, 'dd.MMM.yyyy');
};

const formatDisplayDateTime = (value) => {
  if (!value) return '---';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '---' : format(date, 'dd.MMM.yyyy HH:mm');
};

const parseActionMetadata = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const extractMelCdlReference = (...values) => {
  const combined = values.filter(Boolean).join(' ');
  const matches = combined.match(/\b\d{2}-\d{2}-\d{2}[A-Z]?\b/g);
  return matches?.[0] || '';
};

const formatPreciseToGo = (dueAt) => {
  if (!dueAt) return '';
  const dueDate = new Date(dueAt);
  if (Number.isNaN(dueDate.getTime())) return '';
  const totalHours = Math.max(0, Math.ceil((dueDate.getTime() - Date.now()) / 3600000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days && hours) return `${days}d ${hours}h`;
  if (days) return `${days}d 0h`;
  return `${hours}h`;
};

const formatDeferralType = (metadata, complaint) => {
  const category = String(complaint?.deferral_category || metadata.category || '').toUpperCase();
  const itemCategory = String(complaint?.deferral_type || metadata.deferral_type || '').toUpperCase();
  if (!category && !itemCategory) return '';
  if (category === 'CDL') return itemCategory ? `CDL ${itemCategory}` : 'CDL';
  if (category === 'DD') return itemCategory ? `DD ${itemCategory}` : 'DD';
  return itemCategory ? `MEL ${itemCategory}` : 'MEL';
};

const SectionTitle = ({ children }) => (
  <h3 className="briefing-section-title">{children}</h3>
);

const EmptyStrip = ({ label }) => (
  <div className="briefing-empty-strip">{label || 'no items'}</div>
);

const CompactInfoRow = ({ icon, label, rightText = 'no items' }) => (
  <div className="briefing-compact-row">
    <div className="briefing-compact-left">
      <img src={icon} alt="" className="asr-inline-icon" />
      <span>{label}</span>
    </div>
    <div className="briefing-compact-right">{rightText}</div>
  </div>
);

const NextDueEventsCard = ({ items }) => (
  <div className="briefing-block-card">
    <div className="briefing-block-header">Open Items</div>
    {items?.length ? (
      <div className="briefing-open-items-list">
        {items.map(item => (
          <div key={item.id} className="briefing-open-item">
            <div className="briefing-open-item-wo">{item.work_order_number || `WO.${item.id}`}</div>
            <div className="briefing-open-item-desc">{item.defect_description}</div>
          </div>
        ))}
      </div>
    ) : (
      <EmptyStrip />
    )}
  </div>
);

const DowngradeCard = ({ item }) => (
  <div className="briefing-block-card briefing-block-card-tight" key={item.id}>
    <div className="briefing-card-headline">
      <img src={mccIcon} alt="" className="asr-inline-icon" />
      <span>Aircraft Downgrades/Limitations</span>
    </div>
    <div className="briefing-downgrade-box">
      <div className="briefing-downgrade-pair">
        <span className="briefing-mini-label">Type.</span>
        <span className="briefing-mini-value">{item.type || item.category || item.section || '---'}</span>
      </div>
      <div className="briefing-downgrade-pair">
        <span className="briefing-mini-label">Workorder IDs.</span>
        <span className="briefing-mini-value">{item.work_order_number || `WO.${item.id}`}</span>
      </div>
    </div>
  </div>
);

const isComplaintOverdue = (complaint) => {
  if (!complaint?.due_at) return false;
  const dueDate = new Date(complaint.due_at);
  return !Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now();
};

const DeferredComplaintCard = ({ title, complaint, onClick }) => (
  <div className="briefing-complaint-wrap" key={complaint.id}>
    {title ? <div className="briefing-list-title">{title}</div> : null}
    <button
      type="button"
      className={`briefing-complaint-card briefing-complaint-card-button ${isComplaintOverdue(complaint) ? 'is-overdue' : 'is-deferred'}`}
      onClick={onClick}
    >
      <div className="briefing-complaint-main">
        <div className="briefing-complaint-top">
          <span className="briefing-complaint-wo">{complaint.work_order_number || `WO.${complaint.id}`}</span>
          <span className="briefing-complaint-badge">DEFERRED</span>
          <span className="briefing-complaint-tag">{complaint.category === 'Pilot' ? 'Pilot' : 'Maintenance'}</span>
        </div>
        <div className="briefing-complaint-desc">{complaint.defect_description}</div>
        <div className="briefing-complaint-meta">
          <div className="briefing-meta-item">
            <span className="briefing-meta-label">Opened</span>
            <span className="briefing-meta-value">{formatDisplayDate(complaint.opened_at)}</span>
          </div>
          <div className="briefing-meta-item">
            <span className="briefing-meta-label">Due at</span>
            <span className="briefing-meta-value">
              {complaint.due_at ? `${formatDisplayDate(complaint.due_at)}${complaint.due_hours ? ` (${complaint.due_hours} H)` : ''}` : '---'}
            </span>
          </div>
        </div>
      </div>
      <Chevron />
    </button>
  </div>
);

const CrsRow = ({ lastCRS }) => (
  <div className="briefing-inline-row">
    <div className="briefing-inline-left">
      <img src={mccIcon} alt="" className="asr-inline-icon" />
      <span>Last Crs for {lastCRS?.work_order_number || 'WO.0000000'}</span>
    </div>
    <div className="briefing-inline-right">
      <span>approved LT.{lastCRS?.id || '---'} at {lastCRS?.location || '---'} on {formatDisplayDateTime(lastCRS?.performed_at)} UTC</span>
      <Chevron />
    </div>
  </div>
);

const RowButton = ({ children, className = '', onClick }) => (
  <button type="button" className={`briefing-inline-row briefing-inline-row-button ${className}`.trim()} onClick={onClick}>
    {children}
  </button>
);

const ClosedItemsRow = ({ count, onClick }) => (
  <RowButton onClick={onClick}>
    <div className="briefing-inline-left">
      <img src={openComplaintsIcon} alt="" className="asr-inline-icon" />
      <span>Closed Items</span>
    </div>
    <div className="briefing-inline-right">
      <span className="briefing-muted-inline">Recently Closed</span>
      <span className="briefing-strong-inline">{count || 0}</span>
      <Chevron />
    </div>
  </RowButton>
);

const CabinItemsRow = ({ counts, onClick }) => (
  <RowButton className="briefing-inline-row-stats" onClick={onClick}>
    <div className="briefing-inline-left">
      <img src={cabinItemsIcon} alt="" className="asr-inline-icon" />
      <span>Cabin Items</span>
    </div>
    <div className="briefing-inline-stats">
      <div className="briefing-stat-col"><span>Open</span><strong>{counts.open}</strong></div>
      <div className="briefing-stat-col"><span>Accepted</span><strong>{counts.accepted}</strong></div>
      <div className="briefing-stat-col"><span>Deferred</span><strong>{counts.deferred}</strong></div>
      <div className="briefing-stat-col"><span>Recently Closed</span><strong>{counts.closed}</strong></div>
      <Chevron />
    </div>
  </RowButton>
);

const getDateForListType = (complaint, listType) => {
  if (listType === 'closed') return complaint.closed_at || complaint.updated_at || complaint.opened_at || null;
  return complaint.opened_at || complaint.updated_at || complaint.closed_at || null;
};

const getFilterValue = (complaint, fieldId) => {
  switch (fieldId) {
    case 'work_order_number':
      return `${complaint.work_order_number || ''} ${complaint.id || ''}`;
    case 'section':
      return complaint.section || '';
    case 'status':
      return complaint.status || '';
    case 'mcc_reference':
      return complaint.mcc_reference || '';
    case 'ata_chapter':
      return complaint.ata_chapter || '';
    case 'type':
      return `${complaint.type || ''} ${complaint.category || ''}`;
    default:
      return '';
  }
};

const CalendarField = ({
  fieldKey,
  label,
  value,
  isOpen,
  visibleMonth,
  onToggle,
  onChangeMonth,
  onSelectDate,
  onClear
}) => {
  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(visibleMonth);
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 })
  });

  return (
    <div className={`briefing-calendar-field ${fieldKey === 'to' ? 'align-right' : 'align-left'} ${isOpen ? 'open' : ''}`}>
      <button
        type="button"
        className="briefing-browser-date-trigger"
        onClick={() => onToggle(fieldKey)}
      >
        <span className={`briefing-browser-date-value ${value ? 'has-value' : ''}`}>
          {value ? format(parseISO(value), 'dd.MMM.yyyy') : label}
        </span>
        <span className={`briefing-browser-filter-chevron ${isOpen ? 'open' : ''}`}>▾</span>
      </button>

      <div className={`briefing-calendar-popover ${isOpen ? 'open' : ''}`}>
        <div className="briefing-calendar-head">
          <button type="button" className="briefing-calendar-nav" onClick={() => onChangeMonth(fieldKey, -1)}>‹</button>
          <span className="briefing-calendar-title">{format(visibleMonth, 'MMMM yyyy')}</span>
          <button type="button" className="briefing-calendar-nav" onClick={() => onChangeMonth(fieldKey, 1)}>›</button>
        </div>

        <div className="briefing-calendar-weekdays">
          {CALENDAR_WEEKDAYS.map(day => <span key={day}>{day}</span>)}
        </div>

        <div className="briefing-calendar-grid">
          {calendarDays.map(day => (
            <button
              key={day.toISOString()}
              type="button"
              className={[
                'briefing-calendar-day',
                isSameMonth(day, visibleMonth) ? 'current-month' : 'outside-month',
                selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''
              ].join(' ')}
              onClick={() => onSelectDate(fieldKey, day)}
            >
              {format(day, 'd')}
            </button>
          ))}
        </div>

        <div className="briefing-calendar-footer">
          <button type="button" className="briefing-calendar-clear" onClick={() => onClear(fieldKey)}>Clear</button>
        </div>
      </div>
    </div>
  );
};

const AircraftStatusReport = ({
  aircraftId,
  onFlyAircraft,
  showFlyButton = true,
  embeddedMode = false,
  onBack,
  backLabel = 'BRIEFING',
  initialSection = 'main',
  rootTitle
}) => {
  const [report, setReport] = useState(null);
  const [allComplaints, setAllComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('main');
  const [listType, setListType] = useState('closed');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintActions, setComplaintActions] = useState([]);
  const [detailReturnView, setDetailReturnView] = useState('main');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [filterField, setFilterField] = useState('work_order_number');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openCalendar, setOpenCalendar] = useState(null);
  const [calendarMonths, setCalendarMonths] = useState({
    from: new Date(),
    to: new Date()
  });
  const filterDropdownRef = useRef(null);
  const calendarWrapRef = useRef(null);

  useEffect(() => {
    if (initialSection === 'closed') {
      setListType('closed');
      setView('list');
      setSelectedComplaint(null);
      setComplaintActions([]);
      setFilterDropdownOpen(false);
      setOpenCalendar(null);
      return;
    }

    if (initialSection === 'cabin') {
      setListType('cabin');
      setView('list');
      setSelectedComplaint(null);
      setComplaintActions([]);
      setFilterDropdownOpen(false);
      setOpenCalendar(null);
      return;
    }

    setView('main');
    setSelectedComplaint(null);
    setComplaintActions([]);
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  }, [initialSection]);

  useEffect(() => {
    if (!aircraftId) return;
    setLoading(true);
    Promise.all([
      getAircraftStatusReport(aircraftId),
      getComplaints(aircraftId)
    ])
      .then(([statusRes, complaintsRes]) => {
        setReport(statusRes.data.data);
        setAllComplaints(complaintsRes.data.complaints || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [aircraftId]);

  const reloadAircraftData = async () => {
    if (!aircraftId) return;
    const [statusRes, complaintsRes] = await Promise.all([
      getAircraftStatusReport(aircraftId),
      getComplaints(aircraftId)
    ]);
    setReport(statusRes.data.data);
    setAllComplaints(complaintsRes.data.complaints || []);
  };

  useEffect(() => {
    const onMouseDown = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (calendarWrapRef.current && !calendarWrapRef.current.contains(event.target)) {
        setOpenCalendar(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const closedItems = useMemo(() => (
    allComplaints
      .filter(item => item.status === 'CLOSED')
      .sort((a, b) => new Date(getDateForListType(b, 'closed')) - new Date(getDateForListType(a, 'closed')))
  ), [allComplaints]);

  const cabinItems = useMemo(() => (
    allComplaints
      .filter(item => item.type === 'Cabin' || item.category === 'Cabin')
      .sort((a, b) => new Date(getDateForListType(b, 'cabin')) - new Date(getDateForListType(a, 'cabin')))
  ), [allComplaints]);

  const recentClosedCount = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return closedItems.filter(item => {
      const date = new Date(item.closed_at || item.updated_at || item.opened_at || 0);
      return !Number.isNaN(date.getTime()) && date >= cutoff;
    }).length;
  }, [closedItems]);

  const cabinCounts = useMemo(() => ({
    open: cabinItems.filter(item => item.status === 'OPEN').length,
    accepted: cabinItems.filter(item => item.status === 'ACCEPTED').length,
    deferred: cabinItems.filter(item => item.status === 'DEFERRED').length,
    closed: cabinItems.filter(item => item.status === 'CLOSED').length
  }), [cabinItems]);

  const activeItems = listType === 'closed' ? closedItems : cabinItems;

  const filterOptionCounts = useMemo(() => (
    FILTER_OPTIONS.reduce((acc, option) => {
      acc[option.id] = activeItems.filter(item => String(getFilterValue(item, option.id)).trim()).length;
      return acc;
    }, {})
  ), [activeItems]);

  const filteredItems = useMemo(() => {
    return activeItems.filter(item => {
      const filterValue = String(getFilterValue(item, filterField)).toLowerCase();
      const matchesSearch = !searchTerm.trim() || filterValue.includes(searchTerm.trim().toLowerCase());

      const listDateValue = getDateForListType(item, listType);
      const listDate = listDateValue ? new Date(listDateValue) : null;
      const listDateKey = listDate && !Number.isNaN(listDate.getTime()) ? format(listDate, 'yyyy-MM-dd') : '';
      const matchesFrom = !dateFrom || (listDateKey && listDateKey >= dateFrom);
      const matchesTo = !dateTo || (listDateKey && listDateKey <= dateTo);

      return matchesSearch && matchesFrom && matchesTo;
    });
  }, [activeItems, dateFrom, dateTo, filterField, listType, searchTerm]);

  const openListView = (nextListType) => {
    setListType(nextListType);
    setSelectedComplaint(null);
    setComplaintActions([]);
    setView('list');
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  };

  const openDetailView = async (complaint, returnView = view) => {
    setSelectedComplaint(complaint);
    setComplaintActions([]);
    setDetailReturnView(returnView);
    setView('detail');
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
    if (!complaint?.id) return;
    const [complaintResult, actionsResult] = await Promise.allSettled([
      getComplaint(complaint.id),
      getComplaintActions(complaint.id)
    ]);
    if (complaintResult.status === 'fulfilled') {
      const complaintRes = complaintResult.value;
      setSelectedComplaint(complaintRes.data.complaint || complaint);
    } else {
      console.error(complaintResult.reason);
    }
    if (actionsResult.status === 'fulfilled') {
      setComplaintActions(actionsResult.value.data.actions || []);
    } else if (actionsResult.reason?.response?.status !== 404) {
      console.error(actionsResult.reason);
    }
  };

  const reloadDetailView = async () => {
    if (!selectedComplaint?.id) return;
    const [complaintResult, actionsResult] = await Promise.allSettled([
      getComplaint(selectedComplaint.id),
      getComplaintActions(selectedComplaint.id)
    ]);
    if (complaintResult.status === 'fulfilled') {
      const complaintRes = complaintResult.value;
      setSelectedComplaint(complaintRes.data.complaint || selectedComplaint);
    } else {
      console.error(complaintResult.reason);
    }
    if (actionsResult.status === 'fulfilled') {
      setComplaintActions(actionsResult.value.data.actions || []);
    } else if (actionsResult.reason?.response?.status !== 404) {
      console.error(actionsResult.reason);
    }
  };

  const handleAddWorkorderAction = async () => {
    if (!selectedComplaint?.id) return;
    const actionText = window.prompt('Action text');
    if (!actionText || !actionText.trim()) return;
    try {
      await updateComplaint(selectedComplaint.id, {
        mcc_reference: selectedComplaint.mcc_reference || null,
        action_type: 'ACTION',
        action_text: actionText.trim()
      });
      await reloadDetailView();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCloseWorkorder = async () => {
    if (!selectedComplaint?.id) return;
    try {
      await updateComplaint(selectedComplaint.id, {
        status: 'CLOSED',
        action_type: 'CLOSE',
        action_text: 'WORKORDER CLOSED.'
      });
      await Promise.all([reloadDetailView(), reloadAircraftData()]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBack = () => {
    if (view === 'detail') {
      setView(detailReturnView || 'main');
      return;
    }
    if (embeddedMode && onBack) {
      onBack();
      return;
    }
    setView('main');
    setSelectedComplaint(null);
    setFilterDropdownOpen(false);
    setOpenCalendar(null);
  };

  const handleCalendarToggle = (fieldKey) => {
    setFilterDropdownOpen(false);
    setOpenCalendar(current => current === fieldKey ? null : fieldKey);
    setCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: fieldKey === 'from' && dateFrom ? parseISO(dateFrom) : fieldKey === 'to' && dateTo ? parseISO(dateTo) : prev[fieldKey]
    }));
  };

  const handleCalendarMonthChange = (fieldKey, direction) => {
    setCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: direction > 0 ? addMonths(prev[fieldKey], 1) : subMonths(prev[fieldKey], 1)
    }));
  };

  const handleCalendarSelect = (fieldKey, day) => {
    const nextValue = format(day, 'yyyy-MM-dd');
    if (fieldKey === 'from') {
      setDateFrom(nextValue);
      if (dateTo && dateTo < nextValue) setDateTo(nextValue);
    } else {
      setDateTo(nextValue);
      if (dateFrom && dateFrom > nextValue) setDateFrom(nextValue);
    }
    setOpenCalendar(null);
  };

  const handleCalendarClear = (fieldKey) => {
    if (fieldKey === 'from') setDateFrom('');
    if (fieldKey === 'to') setDateTo('');
    setOpenCalendar(null);
  };

  if (loading || !report) {
    return (
      <div className="asr-container">
        <div className="spinner" style={{ margin: 'auto' }} />
      </div>
    );
  }

  const selectedFilter = FILTER_OPTIONS.find(option => option.id === filterField);
  const listTitle = listType === 'closed' ? 'Closed Items' : 'Cabin Items';
  const headerTitle = view === 'detail'
    ? 'Workorder Detail'
    : (view === 'main' ? (rootTitle || 'Briefing') : (rootTitle || listTitle));
  const headerBackLabel = view === 'detail' ? 'OVERVIEW' : backLabel;
  const latestDispatchAction = [...complaintActions].reverse().find(action => {
    if (action.action_type === 'DISPATCH') return true;
    const metadata = parseActionMetadata(action.metadata_json);
    return Boolean(metadata.category || metadata.deferral_type);
  });
  const latestDispatchMeta = parseActionMetadata(latestDispatchAction?.metadata_json);
  const displayText = (...values) => values.find(value => {
    const text = String(value ?? '').trim();
    return text && text !== '---';
  }) || 'N/A';
  const workorderIssueStation = displayText(selectedComplaint?.issue_station, selectedComplaint?.deferral_station, latestDispatchMeta.deferral_station);
  const workorderToGo = displayText(formatPreciseToGo(selectedComplaint?.due_at));
  const workorderDefValue = latestDispatchMeta.to_go_days ? `${latestDispatchMeta.to_go_days} Days` : workorderToGo;
  const workorderMelRef = displayText(selectedComplaint?.mel_cdl_ref, extractMelCdlReference(latestDispatchMeta.mel_reference, latestDispatchMeta.document_reference, selectedComplaint?.mcc_reference));
  const workorderDefType = displayText(formatDeferralType(latestDispatchMeta, selectedComplaint));
  const workorderDefStation = displayText(selectedComplaint?.deferral_station, latestDispatchMeta.deferral_station, selectedComplaint?.issue_station);
  const workorderDefReason = displayText(selectedComplaint?.deferral_reason, latestDispatchMeta.deferral_reason);
  const workorderDefDate = displayText(selectedComplaint?.deferral_date, latestDispatchMeta.deferral_date, formatDisplayDateTime(latestDispatchAction?.performed_at), formatDisplayDateTime(selectedComplaint?.updated_at));
  const workorderDefCreation = displayText(selectedComplaint?.deferral_creation, formatDisplayDateTime(latestDispatchAction?.performed_at), formatDisplayDateTime(selectedComplaint?.updated_at));

  return (
    <div className="asr-container">
      <div className="asr-header-shell">
        {view === 'main' && !embeddedMode ? (
          <>
            <div className="asr-page-header">
              <div className="asr-title-pill">Briefing</div>
              {showFlyButton ? (
                <div className="asr-fly-shell">
                  <button className="asr-fly-btn" type="button" onClick={onFlyAircraft}>
                    <span className="asr-fly-btn-text">Fly Aircraft</span>
                  </button>
                </div>
              ) : <div className="asr-header-spacer" />}
            </div>
            <div className="asr-header-lower" />
          </>
        ) : (
          <>
            <div className="asr-page-header asr-page-header-subview">
              <button type="button" className="asr-back-btn" onClick={handleBack}>
                <img src={arrowLeftIcon} alt="" className="asr-back-arrow" />
                {headerBackLabel}
              </button>
              <div className="asr-page-title">{headerTitle}</div>
              <div className="asr-header-spacer" />
            </div>
            <div className="asr-header-lower" />
          </>
        )}
      </div>

      <div className="asr-content">
        {view === 'main' ? (
          <>
            <SectionTitle>Aircraft</SectionTitle>
            <div className="asr-info-card">
              <div className="asr-info-col">
                <span className="asr-info-label">AC Reg.:</span>
                <span className="asr-info-val">{report.aircraft.registration}</span>
              </div>
              <div className="asr-info-col">
                <span className="asr-info-label">AC Verification Code:</span>
                <span className="asr-info-val">{report.aircraft.ac_verification_code}</span>
              </div>
              <div className="asr-error-box">
                No A/C device data could be found for this aircraft. Please inform the responsible authority.
              </div>
            </div>

            <SectionTitle>Next Due Maintenance Events</SectionTitle>
            <NextDueEventsCard items={[]} />

            <SectionTitle>Aircraft Downgrades/Limitations</SectionTitle>
            {report.downgrades?.length ? (
              report.downgrades.map(item => <DowngradeCard key={item.id} item={item} />)
            ) : (
              <CompactInfoRow icon={mccIcon} label="Aircraft Downgrades/Limitations" />
            )}

            <SectionTitle>MEL/CDL Complaints</SectionTitle>
            {report.airworthyDeferred?.length ? (
              report.airworthyDeferred.map(item => (
                <DeferredComplaintCard key={item.id} complaint={item} onClick={() => openDetailView(item, 'main')} />
              ))
            ) : (
              <EmptyStrip />
            )}

            <SectionTitle>DD HIL Complaints</SectionTitle>
            {report.nonAirworthyDeferred?.length ? (
              report.nonAirworthyDeferred.map(item => (
                <DeferredComplaintCard key={item.id} complaint={item} onClick={() => openDetailView(item, 'main')} />
              ))
            ) : (
              <EmptyStrip />
            )}

            <SectionTitle>Technical Service Notices to Crew</SectionTitle>
            {report.techNotices?.length ? (
              report.techNotices.map(item => (
                <CompactInfoRow
                  key={item.id}
                  icon={mccIcon}
                  label={item.work_order_number || item.defect_description || 'Technical Service Notice'}
                  rightText={item.status || 'open'}
                />
              ))
            ) : (
              <CompactInfoRow icon={mccIcon} label="Technical Service Notices to Crew" />
            )}

            <SectionTitle>Last Release to Service (CRS)</SectionTitle>
            {report.lastCRS ? (
              <CrsRow lastCRS={report.lastCRS} />
            ) : (
              <CompactInfoRow icon={mccIcon} label="Last Crs" />
            )}

            <SectionTitle>Other Information</SectionTitle>
            <ClosedItemsRow count={recentClosedCount} onClick={() => openListView('closed')} />
            <CabinItemsRow counts={cabinCounts} onClick={() => openListView('cabin')} />
          </>
        ) : view === 'list' ? (
          <>
            <div className="briefing-browser-toolbar">
              <div className="briefing-browser-filter-cluster">
                <span className="briefing-browser-label">Filter</span>
                <div className="briefing-browser-filter-dropdown" ref={filterDropdownRef}>
                  <button
                    type="button"
                    className="briefing-browser-filter-trigger"
                    onClick={() => setFilterDropdownOpen(open => !open)}
                  >
                    <span>{selectedFilter?.label || 'Filter'}</span>
                    <span className={`briefing-browser-filter-chevron ${filterDropdownOpen ? 'open' : ''}`}>▾</span>
                  </button>
                  <div className={`briefing-browser-filter-menu ${filterDropdownOpen ? 'open' : ''}`}>
                    {FILTER_OPTIONS.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        className={`briefing-browser-filter-option ${filterField === option.id ? 'active' : ''}`}
                        onClick={() => {
                          setFilterField(option.id);
                          setFilterDropdownOpen(false);
                        }}
                      >
                        <span>{option.label}</span>
                        <span>{filterOptionCounts[option.id] || 0}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  className="briefing-browser-search"
                  placeholder="Search for..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="briefing-browser-date-cluster" ref={calendarWrapRef}>
                <span className="briefing-browser-label">Date</span>
                <CalendarField
                  fieldKey="from"
                  label="From"
                  value={dateFrom}
                  isOpen={openCalendar === 'from'}
                  visibleMonth={calendarMonths.from}
                  onToggle={handleCalendarToggle}
                  onChangeMonth={handleCalendarMonthChange}
                  onSelectDate={handleCalendarSelect}
                  onClear={handleCalendarClear}
                />
                <span className="briefing-browser-date-sep">-</span>
                <CalendarField
                  fieldKey="to"
                  label="To"
                  value={dateTo}
                  isOpen={openCalendar === 'to'}
                  visibleMonth={calendarMonths.to}
                  onToggle={handleCalendarToggle}
                  onChangeMonth={handleCalendarMonthChange}
                  onSelectDate={handleCalendarSelect}
                  onClear={handleCalendarClear}
                />
              </div>
            </div>

            <div className="briefing-browser-results">
              {filteredItems.length ? filteredItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className="briefing-browser-item"
                  onClick={() => openDetailView(item)}
                >
                  <div className="briefing-browser-item-main">
                    <div className="briefing-browser-item-top">
                      <span className="briefing-browser-item-wo">{item.work_order_number || `WO.${item.id}`}</span>
                      <span className="briefing-browser-item-badge">{item.status || '---'}</span>
                      <span className="briefing-browser-item-tag">{item.type || item.category || 'Item'}</span>
                    </div>
                    <div className="briefing-browser-item-desc">{item.defect_description || 'No description provided'}</div>
                    <div className="briefing-browser-item-meta">
                      <div className="briefing-browser-meta-field">
                        <span>Opened</span>
                        <strong>{formatDisplayDate(item.opened_at)}</strong>
                      </div>
                      <div className="briefing-browser-meta-field">
                        <span>{listType === 'closed' ? 'Closed' : 'Current Date'}</span>
                        <strong>{formatDisplayDate(getDateForListType(item, listType))}</strong>
                      </div>
                      <div className="briefing-browser-meta-field">
                        <span>ATA</span>
                        <strong>{item.ata_chapter || '---'}</strong>
                      </div>
                    </div>
                  </div>
                  <Chevron />
                </button>
              )) : (
                <EmptyStrip label="no matching items" />
              )}
            </div>
          </>
        ) : (
          <div className="workorder-detail-page">
            <div className={`workorder-detail-card ${isComplaintOverdue(selectedComplaint) ? 'is-overdue' : 'is-deferred'}`}>
              <div className="workorder-detail-main">
                <div className="workorder-detail-top">
                  <span className="workorder-detail-wo">{selectedComplaint?.work_order_number || `WO.${selectedComplaint?.id || '---'}`}</span>
                  <div className="workorder-detail-badges">
                    <span className="workorder-status-badge">{selectedComplaint?.status || '---'}</span>
                    <span className="workorder-type-badge">{selectedComplaint?.type || selectedComplaint?.category || 'Pilot'}</span>
                  </div>
                </div>

                <div className="workorder-detail-desc">{selectedComplaint?.defect_description || 'No description provided'}</div>

                <div className="workorder-detail-meta-grid">
                  <div className="workorder-meta-pair"><span>Opened</span><strong>{formatDisplayDateTime(selectedComplaint?.opened_at)}</strong></div>
                  <div className="workorder-meta-pair"><span>Due at</span><strong>{selectedComplaint?.due_at ? formatDisplayDate(selectedComplaint.due_at) : 'N/A'}</strong></div>
                  <div className="workorder-meta-pair"><span>Issue Station</span><strong>{workorderIssueStation}</strong></div>
                  <div className="workorder-meta-pair"><span>To Go</span><strong>{workorderToGo}</strong></div>
                  <div className="workorder-meta-pair"><span>ATA</span><strong>{selectedComplaint?.ata_chapter || 'N/A'}</strong></div>
                  <div className="workorder-meta-pair"><span>Def. Value</span><strong>{workorderDefValue}</strong></div>
                </div>

                <div className="workorder-deferral-lines">
                  <div className="workorder-deferral-line workorder-deferral-line-ref">
                    <div className="workorder-meta-pair"><span>MEL/CDL Ref</span><strong>{workorderMelRef}</strong></div>
                  </div>
                  <div className="workorder-deferral-line workorder-deferral-line-main">
                    <div className="workorder-meta-pair"><span>Deferral Type</span><strong>{workorderDefType}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Station</span><strong>{workorderDefStation}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Reason</span><strong>{workorderDefReason}</strong></div>
                    <div className="workorder-meta-pair"><span>Def. Date</span><strong>{workorderDefDate}</strong></div>
                  </div>
                  <div className="workorder-deferral-line workorder-deferral-line-creation">
                    <div className="workorder-meta-pair"><span>Def. Creation</span><strong>{workorderDefCreation}</strong></div>
                  </div>
                </div>
              </div>

              <button type="button" className="workorder-reload-btn" onClick={reloadDetailView}>Reload</button>
            </div>

            {selectedComplaint?.status !== 'CLOSED' ? (
              <div className="workorder-detail-actions">
                <button type="button" className="workorder-action-btn" onClick={handleAddWorkorderAction}>Add Action to Last Work Step</button>
                <button type="button" className="workorder-action-btn" onClick={handleCloseWorkorder}>Close Workorder</button>
              </div>
            ) : null}

            <div className="workorder-history-list">
              {complaintActions.length ? complaintActions.map((action, index) => {
                const metadata = parseActionMetadata(action.metadata_json);
                const intervalParts = [
                  metadata.inspection_interval_days ? `${metadata.inspection_interval_days} DAY(S)` : '',
                  metadata.inspection_interval_fh ? `${metadata.inspection_interval_fh} FH` : '',
                  metadata.inspection_interval_fc ? `${metadata.inspection_interval_fc} FC` : ''
                ].filter(Boolean);
                return (
                  <div className="workorder-history-card" key={action.id || index}>
                    <div className="workorder-history-head">
                      <span>{action.action_type === 'STATUS' ? 'Status Update' : `Action ${index + 1}`}</span>
                      <span>{formatDisplayDateTime(action.performed_at)}</span>
                    </div>
                    <strong>{action.action_text || 'Complaint updated.'}</strong>
                    {metadata.repetitive_inspection ? <strong>REPETITIVE INSPECTION CREATED: {metadata.repetitive_inspection_description || 'DO INSPECTION'}</strong> : null}
                    {intervalParts.length ? <strong>REPETITIVE INSPECTION INTERVALS: {intervalParts.join(' / ')}</strong> : null}
                    {metadata.document_reference ? <strong>REFERENCE/REVISION: {metadata.document_reference}</strong> : null}
                    {action.performed_by_name ? <strong>PERFORMED SIGN: {action.performed_by_name}</strong> : null}
                  </div>
                );
              }) : (
                <div className="workorder-history-card">
                  <div className="workorder-history-head">
                    <span>Status Update</span>
                    <span>{formatDisplayDateTime(selectedComplaint?.opened_at)}</span>
                  </div>
                  <strong>WORKORDER OPENED.</strong>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AircraftStatusReport;
